/**
 * PodTube Store - LocalStorage management for channels, listened episodes, and preferences
 */

const STORAGE_KEY_SUBS = "podtube_subscriptions";
const STORAGE_KEY_LISTENED = "podtube_listened";
const STORAGE_KEY_FAVORITES = "podtube_favorites";
const STORAGE_KEY_DURATIONS = "podtube_durations";
const STORAGE_KEY_SETTINGS = "podtube_settings";
const STORAGE_KEY_TAGS = "podtube_tags";
const STORAGE_KEY_NOW_PLAYING = "podtube_now_playing";

const NOW_PLAYING_FIELDS = [
  "id",
  "videoId",
  "channelId",
  "channelTitle",
  "author",
  "title",
  "thumbnail",
  "link",
  "audioUrl",
  "duration",
];

export const DEFAULT_TAGS = [
  "💡 Interesante",
  "💰 Economía",
  "🧠 Desarrollo",
  "🎙️ Entrevista",
  "🔥 Imprescindible",
  "⚡ Ver luego",
];

import { KNOWN_DURATIONS } from "./durationsData.js";
import { CHANNELS } from "./channelsConfig.js";

const OVERRIDABLE_DEFAULTS = { enabled: true, minDuration: null, maxDuration: null };

export function buildSubscriptions(config, saved) {
  const savedList = Array.isArray(saved) ? saved : [];
  const savedById = new Map(savedList.map((s) => [s.id, s]));
  const configIds = new Set(config.map((c) => c.id));

  const configured = config.map((channel) => {
    const sub = { ...OVERRIDABLE_DEFAULTS, ...channel, fromConfig: true };
    const override = savedById.get(channel.id) || {};
    for (const field of Object.keys(OVERRIDABLE_DEFAULTS)) {
      if (field in override) sub[field] = override[field];
    }
    return sub;
  });
  const addedByHand = savedList
    .filter((s) => !configIds.has(s.id) && !s.fromConfig)
    .map((s) => ({ ...s }));

  return [...configured, ...addedByHand];
}

export function serializeSubscriptions(config, subscriptions) {
  const configById = new Map(config.map((c) => [c.id, c]));
  return subscriptions.map((sub) => {
    const channel = configById.get(sub.id);
    if (!channel) return sub;
    const entry = { id: sub.id, fromConfig: true };
    for (const [field, fallback] of Object.entries(OVERRIDABLE_DEFAULTS)) {
      const value = sub[field] ?? fallback;
      if (value !== (channel[field] ?? fallback)) entry[field] = value;
    }
    return entry;
  });
}

export const POPULAR_SUGGESTIONS = [
  {
    id: "UCAAGdEk4YbbkTJZ0IqaAljA",
    title: "Kaizen",
    host: "Jaime Rodríguez de Santiago",
    handle: "@jaimerodriguezdesantiago",
    color: "#3b82f6",
    category: "Modelos mentales & Aprendizaje",
  },
  {
    id: "UCJFP-5V2-0BFeVmKifG0H_Q",
    title: "Tengo un Plan",
    host: "Juan Domínguez & Sergio Beguería",
    handle: "@TengoUnPlanPodcast",
    color: "#10b981",
    category: "Emprendimiento & Finanzas",
  },
  {
    id: "UCWU8fcAN3X8hjMR9k0rbV5g",
    title: "Nadie Sabe Nada",
    host: "Buenafuente & Berto Romero",
    handle: "@NadieSabeNada",
    color: "#f59e0b",
    category: "Humor & Improvisación",
  },
  {
    id: "UCBYyJBCtCvgqA4NwtoPMwpQ",
    title: "The Wild Project",
    host: "Jordi Wild",
    handle: "@TheWildProject",
    color: "#ef4444",
    category: "Entrevistas & Charlas",
  },
  {
    id: "UCYxGNI8Au74jEMY5hZTyNgg",
    title: "Entiende Tu Mente",
    host: "Molo Cebrián",
    handle: "@entiendetumente",
    color: "#8b5cf6",
    category: "Psicología & Crecimiento",
  },
  {
    id: "UCKC77AR_zWXRTE2GOD_2Uag",
    title: "Marc Vidal",
    host: "Marc Vidal",
    handle: "@marc_vidal",
    color: "#06b6d4",
    category: "Economía & Transformación",
  },
  {
    id: "rss:https://thebulwark.substack.com/feed",
    title: "The Bulwark Podcast",
    host: "Tim Miller",
    handle: "thebulwark.substack.com",
    avatar:
      "https://substackcdn.com/image/fetch/f_auto,q_auto:good,fl_progressive:steep/https%3A%2F%2Fbucketeer-e05bbc84-baa3-437e-9518-adb32be77984.s3.amazonaws.com%2Fpublic%2Fimages%2Fbc66e7ea-31fa-4c46-9d29-450ee63c9df0_1400x1400.png",
    color: "#ff6719",
    type: "substack",
    feedUrl: "https://thebulwark.substack.com/feed",
    category: "Substack & Podcasts",
  },
  {
    id: "rss:https://www.thefp.com/feed",
    title: "Honestly with Bari Weiss",
    host: "The Free Press",
    handle: "thefp.com",
    avatar:
      "https://substackcdn.com/image/fetch/f_auto,q_auto:good,fl_progressive:steep/https%3A%2F%2Fbucketeer-e05bbc84-baa3-437e-9518-adb32be77984.s3.amazonaws.com%2Fpublic%2Fimages%2F61c36fbe-fa19-4592-a162-8178330d89e5_1400x1400.png",
    color: "#ff6719",
    type: "substack",
    feedUrl: "https://www.thefp.com/feed",
    category: "Substack & Podcasts",
  },
  {
    id: "rss:https://lexfridman.com/feed/podcast/",
    title: "Lex Fridman Podcast",
    host: "Lex Fridman",
    handle: "lexfridman.com/podcast",
    color: "#8b5cf6",
    type: "rss",
    feedUrl: "https://lexfridman.com/feed/podcast/",
    category: "Substack & Podcasts",
  },
];

class Store {
  constructor() {
    this.subscribers = new Set();
    this.init();
  }

  init() {
    // Subscriptions
    let savedSubs = null;
    try {
      savedSubs = JSON.parse(localStorage.getItem(STORAGE_KEY_SUBS));
    } catch (_e) {}
    this.subscriptions = buildSubscriptions(CHANNELS, savedSubs);
    this.saveSubscriptions();

    // Listened items: { [videoId]: { listenedAt, progress, duration, completed } }
    try {
      this.listened = JSON.parse(localStorage.getItem(STORAGE_KEY_LISTENED) || "{}");
    } catch {
      this.listened = {};
    }

    // Favorites: [videoId, ...]
    try {
      this.favorites = JSON.parse(localStorage.getItem(STORAGE_KEY_FAVORITES) || "[]");
    } catch {
      this.favorites = [];
    }

    // Cached known durations: { [videoId]: seconds }
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY_DURATIONS) || "{}");
      this.durations = { ...KNOWN_DURATIONS, ...saved };
    } catch {
      this.durations = { ...KNOWN_DURATIONS };
    }

    // Tags: { [videoId]: ["💡 Interesante", ...] }
    try {
      this.tags = JSON.parse(localStorage.getItem(STORAGE_KEY_TAGS) || "{}");
    } catch {
      this.tags = {};
    }

    // Settings & Filter preferences
    try {
      const savedSettings = JSON.parse(localStorage.getItem(STORAGE_KEY_SETTINGS) || "{}");
      this.settings = {
        hideShorts: savedSettings.hideShorts !== undefined ? savedSettings.hideShorts : true,
        durationFilter: savedSettings.durationFilter || "all", // 'all' | 'long' (>20m) | 'medium' (10-20m) | 'short' (<10m)
        statusFilter: savedSettings.statusFilter || "unheard", // 'all' | 'unheard' | 'listened' | 'favorites'
        activeChannelId: savedSettings.activeChannelId || "all",
        dateFilter: savedSettings.dateFilter || "all", // 'all' | '24h' | '7d' | '30d' | '90d'
        tagFilter: savedSettings.tagFilter || "all", // 'all' | '<tag>'
        sortBy: savedSettings.sortBy || "date-desc", // 'date-desc' | 'date-asc' | 'duration-desc'
        autoMarkListenedAt: savedSettings.autoMarkListenedAt || 0.9, // 90%
        playbackSpeed: savedSettings.playbackSpeed || 1.0,
        showFilters: savedSettings.showFilters === true,
      };
    } catch {
      this.settings = {
        hideShorts: true,
        durationFilter: "all",
        statusFilter: "unheard",
        activeChannelId: "all",
        dateFilter: "all",
        tagFilter: "all",
        sortBy: "date-desc",
        autoMarkListenedAt: 0.9,
        playbackSpeed: 1.0,
        showFilters: false,
      };
    }
  }

  saveSubscriptions() {
    localStorage.setItem(
      STORAGE_KEY_SUBS,
      JSON.stringify(serializeSubscriptions(CHANNELS, this.subscriptions))
    );
    this.notify();
  }

  saveListened(notify = true) {
    localStorage.setItem(STORAGE_KEY_LISTENED, JSON.stringify(this.listened));
    if (notify) this.notify();
  }

  saveFavorites() {
    localStorage.setItem(STORAGE_KEY_FAVORITES, JSON.stringify(this.favorites));
    this.notify();
  }

  saveDurations(notify = true) {
    localStorage.setItem(STORAGE_KEY_DURATIONS, JSON.stringify(this.durations));
    if (notify) this.notify();
  }

  saveTags() {
    localStorage.setItem(STORAGE_KEY_TAGS, JSON.stringify(this.tags));
    this.notify();
  }

  saveSettings() {
    localStorage.setItem(STORAGE_KEY_SETTINGS, JSON.stringify(this.settings));
    this.notify();
  }

  // --- Subscriptions ---
  getSubscriptions() {
    return this.subscriptions;
  }

  findSubscription(channelId) {
    if (!channelId) return undefined;
    const isRss = channelId.startsWith("rss:") || channelId.startsWith("http");
    const cleanId = isRss ? channelId : channelId.startsWith("UC") ? channelId : `UC${channelId}`;
    return this.subscriptions.find(
      (c) =>
        c.id === channelId ||
        c.id === cleanId ||
        (!isRss &&
          !c.id.startsWith("rss:") &&
          !c.id.startsWith("http") &&
          c.id.replace(/^UC/, "") === channelId.replace(/^UC/, ""))
    );
  }

  addSubscription(channel) {
    if (!channel.id) return false;
    const exists = this.subscriptions.some((c) => c.id === channel.id);
    if (!exists) {
      const isRss = channel.id.startsWith("rss:") || channel.id.startsWith("http");
      this.subscriptions.push({
        id: channel.id,
        title: channel.title || "Canal",
        host: channel.host || "",
        handle: channel.handle || `@${channel.title.toLowerCase().replace(/\s+/g, "")}`,
        avatar: channel.avatar || "",
        color: channel.color || (channel.type === "substack" ? "#ff6719" : "#6366f1"),
        type: channel.type || (isRss ? "rss" : "youtube"),
        feedUrl: channel.feedUrl || (isRss ? channel.id.replace(/^rss:/, "") : undefined),
        enabled: true,
      });
      this.saveSubscriptions();
      return true;
    }
    return false;
  }

  removeSubscription(channelId) {
    this.subscriptions = this.subscriptions.filter((c) => c.id !== channelId);
    if (this.settings.activeChannelId === channelId) {
      this.settings.activeChannelId = "all";
      this.saveSettings();
    }
    this.saveSubscriptions();
  }

  // --- Per-channel duration filter ---
  /**
   * Returns { minMin: number|null, maxMin: number|null } for a channel.
   * Values are in minutes; null means no limit.
   */
  getChannelDurationFilter(channelId) {
    const sub = this.findSubscription(channelId);
    return {
      minMin: sub?.minDuration ?? null,
      maxMin: sub?.maxDuration ?? null,
    };
  }

  /**
   * Saves minDuration/maxDuration (in minutes) for a channel.
   * Pass null to remove the limit.
   */
  updateChannelDurationFilter(channelId, minMin, maxMin) {
    const sub = this.findSubscription(channelId);
    if (!sub) return;
    sub.minDuration = minMin === null || minMin === undefined ? null : Number(minMin);
    sub.maxDuration = maxMin === null || maxMin === undefined ? null : Number(maxMin);
    this.saveSubscriptions();
  }

  resetSubscriptions() {
    this.subscriptions = buildSubscriptions(CHANNELS, null);
    this.saveSubscriptions();
  }

  toggleSubscriptionEnabled(channelId) {
    if (!channelId) return false;
    const sub = this.findSubscription(channelId);
    if (sub) {
      sub.enabled = sub.enabled === undefined ? false : !sub.enabled;
      this.saveSubscriptions();
      return sub.enabled;
    }
    return false;
  }

  isSubscriptionEnabled(channelId) {
    if (!channelId) return false;
    const sub = this.findSubscription(channelId);
    return sub ? sub.enabled !== false : true;
  }

  // --- Listened Status ---
  isListened(videoId) {
    return Boolean(this.listened[videoId] && this.listened[videoId].completed);
  }

  markAsListened(videoId, progress = null, duration = null) {
    const prev = this.listened[videoId] || {};
    this.listened[videoId] = {
      ...prev,
      listenedAt: Date.now(),
      progress: progress !== null ? progress : duration || prev.duration || 0,
      duration: duration || prev.duration || 0,
      completed: true,
    };
    this.saveListened();
  }

  unmarkAsListened(videoId) {
    if (this.listened[videoId]) {
      this.listened[videoId].completed = false;
      this.saveListened();
    }
  }

  toggleListened(videoId, duration = null) {
    if (this.isListened(videoId)) {
      this.unmarkAsListened(videoId);
      return false;
    } else {
      this.markAsListened(videoId, null, duration);
      return true;
    }
  }

  updateProgress(videoId, currentTime, duration) {
    if (!videoId) return;
    const prev = this.listened[videoId] || {};
    const dur = duration || prev.duration || 0;
    const wasCompleted = Boolean(prev.completed);
    const isCompleted =
      wasCompleted || (dur > 0 && currentTime / dur >= this.settings.autoMarkListenedAt);

    this.listened[videoId] = {
      ...prev,
      lastPlayedAt: Date.now(),
      progress: Math.floor(currentTime),
      duration: Math.floor(dur),
      completed: isCompleted,
    };

    if (dur > 0 && !this.durations[videoId]) {
      this.durations[videoId] = Math.floor(dur);
      this.saveDurations(false);
    }

    // Only notify listeners if completion status flipped, avoiding re-render on every second
    const statusFlipped = !wasCompleted && isCompleted;
    this.saveListened(statusFlipped);
  }

  setNowPlaying(item) {
    const entry = Object.fromEntries(
      NOW_PLAYING_FIELDS.filter((field) => item[field] !== undefined).map((field) => [
        field,
        item[field],
      ])
    );
    localStorage.setItem(STORAGE_KEY_NOW_PLAYING, JSON.stringify(entry));
  }

  getNowPlaying() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY_NOW_PLAYING));
    } catch {
      return null;
    }
  }

  clearNowPlaying() {
    localStorage.removeItem(STORAGE_KEY_NOW_PLAYING);
  }

  getProgress(videoId) {
    return this.listened[videoId]?.progress || 0;
  }

  // --- Durations ---
  saveVideoDuration(videoId, duration, notify = true) {
    if (!videoId || !duration) return;
    this.durations[videoId] = duration;
    if (notify) {
      this.saveDurations();
    } else {
      localStorage.setItem(STORAGE_KEY_DURATIONS, JSON.stringify(this.durations));
    }
  }

  getVideoDuration(videoId) {
    return this.durations[videoId] || null;
  }

  // --- Favorites ---
  isFavorite(videoId) {
    return this.favorites.includes(videoId);
  }

  toggleFavorite(videoId) {
    if (this.favorites.includes(videoId)) {
      this.favorites = this.favorites.filter((id) => id !== videoId);
    } else {
      this.favorites.push(videoId);
    }
    this.saveFavorites();
  }

  // --- Tags ---
  getTags(videoId) {
    if (!videoId) return [];
    return this.tags[videoId] || [];
  }

  addTag(videoId, tag) {
    if (!videoId || !tag) return false;
    const cleanTag = tag.trim();
    if (!cleanTag) return false;
    const current = this.getTags(videoId);
    if (!current.includes(cleanTag)) {
      this.tags[videoId] = [...current, cleanTag];
      this.saveTags();
      return true;
    }
    return false;
  }

  removeTag(videoId, tag) {
    if (!videoId || !tag) return false;
    const current = this.getTags(videoId);
    if (current.includes(tag)) {
      this.tags[videoId] = current.filter((t) => t !== tag);
      if (this.tags[videoId].length === 0) {
        delete this.tags[videoId];
      }
      this.saveTags();
      return true;
    }
    return false;
  }

  toggleTag(videoId, tag) {
    const current = this.getTags(videoId);
    if (current.includes(tag)) {
      return this.removeTag(videoId, tag);
    } else {
      return this.addTag(videoId, tag);
    }
  }

  getAllUsedTags() {
    const set = new Set();
    for (const tagList of Object.values(this.tags)) {
      if (Array.isArray(tagList)) {
        for (const t of tagList) {
          set.add(t);
        }
      }
    }
    return Array.from(set).sort();
  }

  getAllSuggestedTags() {
    const set = new Set(DEFAULT_TAGS);
    for (const t of this.getAllUsedTags()) {
      set.add(t);
    }
    return Array.from(set);
  }

  // --- Settings ---
  updateSettings(partial) {
    this.settings = { ...this.settings, ...partial };
    this.saveSettings();
  }

  // --- Backup & Restore ---
  exportBackup() {
    return JSON.stringify(
      {
        version: "1.0",
        exportedAt: new Date().toISOString(),
        subscriptions: serializeSubscriptions(CHANNELS, this.subscriptions),
        listened: this.listened,
        favorites: this.favorites,
        durations: this.durations,
        tags: this.tags,
        settings: this.settings,
      },
      null,
      2
    );
  }

  importBackup(jsonString) {
    try {
      const data = JSON.parse(jsonString);
      if (Array.isArray(data.subscriptions)) {
        this.subscriptions = buildSubscriptions(CHANNELS, data.subscriptions);
        this.saveSubscriptions();
      }
      if (data.listened && typeof data.listened === "object") {
        this.listened = data.listened;
        this.saveListened();
      }
      if (Array.isArray(data.favorites)) {
        this.favorites = data.favorites;
        this.saveFavorites();
      }
      if (data.durations && typeof data.durations === "object") {
        this.durations = { ...this.durations, ...data.durations };
        this.saveDurations();
      }
      if (data.tags && typeof data.tags === "object") {
        this.tags = data.tags;
        this.saveTags();
      }
      if (data.settings && typeof data.settings === "object") {
        this.settings = { ...this.settings, ...data.settings };
        this.saveSettings();
      }
      return true;
    } catch (err) {
      console.error("Error importing backup:", err);
      return false;
    }
  }

  // --- Observer Pattern ---
  subscribe(callback) {
    this.subscribers.add(callback);
    return () => this.subscribers.delete(callback);
  }

  notify() {
    for (const callback of this.subscribers) {
      try {
        callback(this);
      } catch (err) {
        console.error("Store subscriber error:", err);
      }
    }
  }
}

export const store = new Store();
